import { useState } from 'react';

import { useIntl } from 'react-intl';
import { createResource, type RouteResource, usePathParam } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { assert, AssertionError, errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import messages from '../../../common/messages';
import type {
	AiNotificationPolicy,
	AiNotificationPolicyResponse,
	ChangeAiNotificationPolicyOrderParams,
	ChangeAiNotificationPolicyStateParams,
	CreateAiNotificationPolicyParams,
	DeleteAiNotificationPolicyParams,
	UpdateAiNotificationPolicyParams,
} from '../../../common/types';

import {
	changeGroupNotificationPolicyOrderEndpoint,
	createGroupNotificationPolicyEndpoint,
	deleteGroupNotificationPolicyEndpoint,
	disableGroupNotificationPolicyEndpoint,
	enableGroupNotificationPolicyEndpoint,
	getGroupNotificationPolicies,
	updateGroupNotificationPolicyEndpoint,
} from './endpoints';

export const groupNotificationPolicyResource: RouteResource<
	AiNotificationPolicyResponse | undefined
> = createResource({
	type: 'groupNotificationPolicyResource',
	getKey: (routerContext) => {
		const teamId = routerContext.match.params?.teamId;

		return `group-notification-policy/${teamId ?? ''}`;
	},
	getData: async (routerContext) => {
		const teamId = routerContext.match.params?.teamId;

		if (!teamId) {
			return;
		}

		return getGroupNotificationPolicies(teamId);
	},
	isBrowserOnly: true,
});

type GroupNotificationPolicyService = {
	isChangingOrder: boolean;
	isDeleting: boolean;
	isDisabling: boolean;
	isEnabling: boolean;
	isCreating: boolean;
	isUpdating: boolean;
	deletePolicy: (payload: DeleteAiNotificationPolicyParams) => Promise<void>;
	disable: (payload: ChangeAiNotificationPolicyStateParams) => Promise<void>;
	enable: (payload: ChangeAiNotificationPolicyStateParams) => Promise<void>;
	create: (payload: CreateAiNotificationPolicyParams) => Promise<void>;
	updateOrder: (
		payload: ChangeAiNotificationPolicyOrderParams,
		updatedValue: AiNotificationPolicy[],
	) => Promise<void | Response>;
	update: (payload: UpdateAiNotificationPolicyParams) => Promise<void>;
};

export const useGroupNotificationPolicyService = (): GroupNotificationPolicyService => {
	const { data, update } = useResource(groupNotificationPolicyResource);
	const { formatMessage } = useIntl();
	const [teamId] = usePathParam('teamId');
	const defaultCatch = useDefaultCatch();
	const flagApi = useFlags();

	const [isChangingOrder, setIsChangingOrder] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isEnabling, setIsEnabling] = useState(false);
	const [isDisabling, setIsDisabling] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isCreating, setIsCreating] = useState(false);

	assert(
		!!data && !!teamId,
		new AssertionError(
			'useGroupNotificationPolicyService',
			'We should not render useGroupNotificationPolicyService before data is fetched',
		),
	);

	const deletePolicy = ({ id, name }: DeleteAiNotificationPolicyParams): Promise<void> => {
		setIsDeleting(true);

		return deleteGroupNotificationPolicyEndpoint({ id, name, teamId })
			.then(() => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.policyDelete, {
						name,
						type: 'notification',
					}),
				});

				update(() => ({
					...data,
					policies: data.policies.filter((policy) => policy.id !== id),
				}));
			})
			.catch(
				defaultCatch({
					id: 'useGroupNotificationPolicyService-deletePolicy',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.deleteError),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsDeleting(false));
	};

	const enable = (payload: ChangeAiNotificationPolicyStateParams): Promise<void> => {
		setIsEnabling(true);

		return enableGroupNotificationPolicyEndpoint({ ...payload, teamId })
			.then(() => {
				update(() => ({
					...data,
					policies: data.policies.map((policy) =>
						policy.id === payload.id ? { ...policy, enabled: true } : policy,
					),
				}));
			})
			.catch(
				defaultCatch({
					id: 'useGroupNotificationPolicyService-enable',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.enableError),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsEnabling(false));
	};

	const disable = (payload: ChangeAiNotificationPolicyStateParams): Promise<void> => {
		setIsDisabling(true);

		return disableGroupNotificationPolicyEndpoint({ ...payload, teamId })
			.then(() => {
				update(() => ({
					...data,
					policies: data.policies.map((policy) =>
						policy.id === payload.id ? { ...policy, enabled: false } : policy,
					),
				}));
			})
			.catch(
				defaultCatch({
					id: 'useGroupNotificationPolicyService-disable',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.disableError),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsDisabling(false));
	};

	const updateOrder = (
		payload: ChangeAiNotificationPolicyOrderParams,
		updatedValue: AiNotificationPolicy[],
	): Promise<void | Response> => {
		setIsChangingOrder(true);

		update(() => ({
			...data,
			policies: updatedValue,
		}));

		return changeGroupNotificationPolicyOrderEndpoint({ ...payload, teamId })
			.catch(
				defaultCatch({
					id: 'useGroupNotificationPolicyService-updateOrder',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.reorderError, { type: '' }),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsChangingOrder(false));
	};

	const create = (payload: CreateAiNotificationPolicyParams): Promise<void> => {
		setIsCreating(true);

		return createGroupNotificationPolicyEndpoint({ ...payload, teamId })
			.then((res) => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.policyAdded, {
						name: payload.name,
						type: 'notification',
					}),
				});

				const newData: AiNotificationPolicyResponse = {
					...data,
					policies: [...data.policies, res],
				};

				update(() => newData);
			})
			.catch(
				defaultCatch({
					id: 'useGroupNotificationPolicyService-create',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.addError, { type: 'notification' }),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsCreating(false));
	};

	const updatePolicy = (payload: UpdateAiNotificationPolicyParams): Promise<void> => {
		setIsUpdating(true);

		return updateGroupNotificationPolicyEndpoint({ ...payload, teamId })
			.then((res) => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.policyEdit, {
						name: payload.name,
						type: 'notification',
					}),
				});

				const newData: AiNotificationPolicyResponse = {
					...data,
					policies: data.policies.map((policy) => {
						if (policy.id === payload.id) {
							return res;
						}

						return policy;
					}),
				};

				update(() => newData);
			})
			.catch(
				defaultCatch({
					id: 'useGroupNotificationPolicyService-updatePolicy',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.editError, {
								type: 'notification',
							}),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsUpdating(false));
	};

	return {
		isCreating,
		isDeleting,
		isEnabling,
		isDisabling,
		isUpdating,
		isChangingOrder,
		deletePolicy,
		enable,
		disable,
		updateOrder,
		create,
		update: updatePolicy,
	};
};
